import { Form, Head, usePage } from "@inertiajs/react"
import type { ReactNode } from "react"
import { useState } from "react"
import { toast } from "sonner"

import InputError from "@/components/input-error"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import MatchLayout from "@/layouts/match-layout"

interface MatchData {
  id: number
  date: string
  kickoff: string
  venue: string
  referee: string
  status: string
  competition: string | null
  weather: string | null
  pitch: string | null
  attendance: number | null
  home_score: number
  away_score: number
  video_url: string | null
  possession_home: number
  possession_away: number
  home_team: { id: number; name: string }
  away_team: { id: number; name: string }
}

interface MetricData {
  id: number
  name: string
  unit: string | null
}

interface MatchStatData {
  id: number
  metric_id: number
  metric_name: string
  home_value: number
  away_value: number
}

export default function StaffMatchesEdit() {
  const { match, metrics, match_stats } = usePage().props as unknown as {
    match: MatchData
    metrics: MetricData[]
    match_stats: MatchStatData[]
  }

  const [activeSection, setActiveSection] = useState<"info" | "stats">("info")

  const usedMetricIds = new Set(match_stats.map((s) => s.metric_id))
  const availableMetrics = metrics.filter((m) => !usedMetricIds.has(m.id))

  return (
    <>
      <Head
        title={`Edit: ${match.home_team.name} vs ${match.away_team.name}`}
      />
      <div className="bg-card border-b">
        <div className="mx-auto max-w-4xl px-4 py-6">
          <h1 className="text-2xl font-bold tracking-tight">
            Edit Match: {match.home_team.name} vs {match.away_team.name}
          </h1>
          <div className="mt-3 flex gap-2">
            <button
              onClick={() => setActiveSection("info")}
              className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                activeSection === "info"
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted text-muted-foreground hover:text-foreground"
              }`}
            >
              Match Info
            </button>
            <button
              onClick={() => setActiveSection("stats")}
              className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                activeSection === "stats"
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted text-muted-foreground hover:text-foreground"
              }`}
            >
              Match Stats
            </button>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-4xl px-4 py-6">
        {activeSection === "info" && (
          <Form
            method="put"
            action={`/staff/matches/${match.id}`}
            onSuccess={() => toast("Match updated successfully")}
          >
            {({ errors, processing }) => (
              <div className="flex flex-col gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Match Details</CardTitle>
                  </CardHeader>
                  <CardContent className="grid gap-4 md:grid-cols-2">
                    <div className="grid gap-2">
                      <Label htmlFor="competition">Competition</Label>
                      <Input
                        id="competition"
                        name="competition"
                        defaultValue={match.competition ?? ""}
                      />
                      <InputError messages={errors.competition} />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="date">Date</Label>
                      <Input
                        id="date"
                        name="date"
                        type="date"
                        defaultValue={match.date}
                      />
                      <InputError messages={errors.date} />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="kickoff">Kickoff</Label>
                      <Input
                        id="kickoff"
                        name="kickoff"
                        defaultValue={match.kickoff}
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="venue">Venue</Label>
                      <Input
                        id="venue"
                        name="venue"
                        defaultValue={match.venue}
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="referee">Referee</Label>
                      <Input
                        id="referee"
                        name="referee"
                        defaultValue={match.referee}
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="video_url">Video URL</Label>
                      <Input
                        id="video_url"
                        name="video_url"
                        defaultValue={match.video_url ?? ""}
                      />
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Score & Possession</CardTitle>
                  </CardHeader>
                  <CardContent className="grid gap-4 md:grid-cols-3">
                    <div className="grid gap-2">
                      <Label>{match.home_team.name} Score</Label>
                      <Input
                        type="number"
                        name="home_score"
                        defaultValue={match.home_score}
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label>{match.away_team.name} Score</Label>
                      <Input
                        type="number"
                        name="away_score"
                        defaultValue={match.away_score}
                      />
                    </div>
                    <div />
                    <div className="grid gap-2">
                      <Label>Possession {match.home_team.name}</Label>
                      <Input
                        type="number"
                        name="possession_home"
                        defaultValue={match.possession_home}
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label>Possession {match.away_team.name}</Label>
                      <Input
                        type="number"
                        name="possession_away"
                        defaultValue={match.possession_away}
                      />
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Conditions</CardTitle>
                  </CardHeader>
                  <CardContent className="grid gap-4 md:grid-cols-3">
                    <div className="grid gap-2">
                      <Label htmlFor="weather">Weather</Label>
                      <Input
                        id="weather"
                        name="weather"
                        defaultValue={match.weather ?? ""}
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="pitch">Pitch</Label>
                      <Input
                        id="pitch"
                        name="pitch"
                        defaultValue={match.pitch ?? ""}
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="attendance">Attendance</Label>
                      <Input
                        id="attendance"
                        name="attendance"
                        type="number"
                        defaultValue={match.attendance ?? ""}
                      />
                    </div>
                  </CardContent>
                </Card>

                <Button type="submit" disabled={processing}>
                  {processing ? "Saving..." : "Save Match Info"}
                </Button>
              </div>
            )}
          </Form>
        )}

        {activeSection === "stats" && (
          <Card>
            <CardHeader>
              <CardTitle>Match Statistics</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col gap-3">
                {match_stats.map((stat) => (
                  <div
                    key={stat.id}
                    className="flex items-center gap-3 rounded-lg border p-3"
                  >
                    <span className="w-40 shrink-0 text-sm font-medium">
                      {stat.metric_name}
                    </span>
                    <Form
                      method="patch"
                      action={`/staff/matches/${match.id}/match_stats/${stat.id}`}
                      onSuccess={() => toast("Stat updated")}
                      className="flex items-center gap-2"
                    >
                      {({ processing }) => (
                        <>
                          <Label className="text-muted-foreground text-xs whitespace-nowrap">
                            Home:
                          </Label>
                          <Input
                            type="number"
                            name="home_value"
                            className="w-20"
                            defaultValue={stat.home_value}
                          />
                          <Label className="text-muted-foreground text-xs whitespace-nowrap">
                            Away:
                          </Label>
                          <Input
                            type="number"
                            name="away_value"
                            className="w-20"
                            defaultValue={stat.away_value}
                          />
                          <Button
                            type="submit"
                            size="sm"
                            variant="outline"
                            disabled={processing}
                          >
                            Save
                          </Button>
                        </>
                      )}
                    </Form>
                    <Form
                      method="delete"
                      action={`/staff/matches/${match.id}/match_stats/${stat.id}`}
                      onSuccess={() => toast("Stat removed")}
                    >
                      {({ processing }) => (
                        <Button
                          type="submit"
                          size="sm"
                          variant="destructive"
                          disabled={processing}
                        >
                          ×
                        </Button>
                      )}
                    </Form>
                  </div>
                ))}
              </div>

              {availableMetrics.length > 0 && (
                <div className="mt-6 border-t pt-4">
                  <Form
                    method="post"
                    action={`/staff/matches/${match.id}/match_stats`}
                    onSuccess={() => {
                      toast("Stat added")
                    }}
                  >
                    {({ processing }) => (
                      <div className="flex items-end gap-3">
                        <div className="grid gap-1">
                          <Label className="text-xs">Metric</Label>
                          <select
                            name="metric_id"
                            className="bg-background h-9 rounded-md border px-2 text-sm"
                            defaultValue=""
                          >
                            <option value="" disabled>
                              Select...
                            </option>
                            {availableMetrics.map((m) => (
                              <option key={m.id} value={m.id}>
                                {m.name}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="grid gap-1">
                          <Label className="text-xs">Home</Label>
                          <Input
                            type="number"
                            name="home_value"
                            className="w-20"
                            defaultValue="0"
                          />
                        </div>
                        <div className="grid gap-1">
                          <Label className="text-xs">Away</Label>
                          <Input
                            type="number"
                            name="away_value"
                            className="w-20"
                            defaultValue="0"
                          />
                        </div>
                        <Button type="submit" size="sm" disabled={processing}>
                          Add Stat
                        </Button>
                      </div>
                    )}
                  </Form>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </>
  )
}

StaffMatchesEdit.layout = (page: ReactNode) => <MatchLayout>{page}</MatchLayout>
